import React, { useCallback, useMemo, useState } from 'react';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { Delete, RadioButtonUnchecked as CheckCircleUnchecked, CheckCircle } from '@material-ui/icons';
import { v4 as uuid } from 'uuid';
import { useLiveRef } from 'ui-utils';

import SelectField from 'components/SelectField';
import TextField, { TextFieldProps } from './TextField';
import IconButton, { IconButtonProps } from './IconButton';
import Button, { ButtonProps } from './Button';
import Checkbox from './Checkbox';
import Typography from './Typography';

const useStyles = (theme: Theme) =>
  createStyles({
    container: {
      display: 'grid',
      gridTemplateColumns: 'auto 20% 1fr auto',
      columnGap: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 2,
      alignItems: 'end',
    },
  });

export type FeedbackLinkState = Array<{ enabled: boolean; type: string; url: string; key?: string }>;

type FeedbackLinkStateUpdater = (value: FeedbackLinkState) => void;

type FieldTypeNames = 'url' | 'type';

type UseFeedbackLink = {
  initialState?: FeedbackLinkState;
  controller?: [FeedbackLinkState, FeedbackLinkStateUpdater] | [];
  onChange?: FeedbackLinkStateUpdater;
};

export interface FeedbackLinkProps extends WithStyles<typeof useStyles> {
  value: FeedbackLinkState | [];
  onChange: (event: React.SyntheticEvent, eventInfo: { index: number; type: FieldTypeNames }) => void;
  onRemove: (event: React.SyntheticEvent, eventInfo: { index: number }) => void;
  onAdd: () => void;
  onSelect: (event: React.SyntheticEvent, eventInfo: { index: number }) => void;
  textFieldProps?: (index: number) => Partial<TextFieldProps>;
  iconButtonProps?: IconButtonProps;
  buttonProps?: Partial<ButtonProps>;
  options?: Array<{ value: string; label: string }>;
}

export const feedbackLinkPromoterUrls = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'yelp', label: 'Yelp' },
  { value: 'patientfusion', label: 'PatientFusion' },
  { value: 'betterdoctor', label: 'BetterDoctor' },
  { value: 'zocdoc', label: 'ZocDoc' },
  { value: 'google', label: 'Google' },
  { value: 'generic', label: 'Generic' },
  { value: 'healthgrades', label: 'Healthgrades' },
  { value: 'ratemds', label: 'RateMDs' },
];

export const feedbackLinkDetractorUrls = [{ value: 'google', label: 'Google' }];

const FeedbackLinks: React.FC<FeedbackLinkProps> = ({
  classes,
  value = [],
  onChange,
  onRemove,
  onAdd,
  onSelect,
  options = feedbackLinkPromoterUrls,
  textFieldProps = () => {},
  buttonProps,
  iconButtonProps,
}) => (
  <div>
    {value.map(({ enabled, type, url, key }, index) => (
      <div key={key || index} className={classes.container}>
        <Checkbox
          color="default"
          checked={enabled}
          icon={<CheckCircleUnchecked />}
          checkedIcon={<CheckCircle />}
          onChange={(event) => onSelect(event, { index })}
        />
        <SelectField
          value={type}
          rounded
          // @ts-ignore
          onChange={(event) => onChange(event, { index, type: 'type' })}
          options={options}
        />
        <TextField
          color="default"
          label="URL"
          rounded
          required
          value={url}
          onChange={(event) => onChange(event, { index, type: 'url' })}
          InputLabelProps={{
            shrink: true,
          }}
          {...textFieldProps(index)}
        />
        <IconButton onClick={(event) => onRemove(event, { index })} {...iconButtonProps}>
          <Delete />
        </IconButton>
      </div>
    ))}
    <Button onClick={onAdd} variant="outlined" rounded {...buttonProps}>
      <Typography>Add item</Typography>
    </Button>
  </div>
);

/**
 * Helper hook for the FeedbackLink component that takes care of the boilerplate that would come with implementing the add, remove and change text logic.
 * NOTE: This hook it not necessary to use the component at all. It's just a bundle of boilerplate bussiness logic to help implementation.
 * @param {UseFeedbackLink} configs - The configs to make the component controlled of not.
 * @param {UseFeedbackLink['initialState']} configs.initialState - The initial state for the component if you want to not control the state.
 * @param {UseFeedbackLink['controller']} configs.controller - A pair of state and state updater so that you can control the state from outside.
 * @param {UseFeedbackLink['onChange']} configs.onChange - A callback that will listen to any changes to the components state add, remove, text changes.
 */

export function useFeedbackLink({ initialState = [], controller = [], onChange = () => {} }: UseFeedbackLink = {}) {
  const [feedbackLinks, setFeedbackLinks] = useState(() => initialState.map((item) => ({ ...item, key: uuid() })));
  const [state, stateUpdater] = controller;

  const cbRef = useLiveRef(onChange);

  const stateToUse = (stateUpdater ? state : feedbackLinks) as FeedbackLinkState;
  const updater = (stateUpdater || setFeedbackLinks) as FeedbackLinkStateUpdater;

  const handleChange = useCallback(
    (event, { index, type }: { index: number; type: FieldTypeNames }) => {
      const value = typeof event === 'string' ? event : event.target.value;
      const newFeedbackLink = [...stateToUse].map((item, linkIndex) => {
        if (index === linkIndex) return { ...item, [type]: value };
        return item;
      });
      cbRef.current(newFeedbackLink);
      updater(newFeedbackLink);
    },
    [updater, cbRef, stateToUse],
  );

  const handleRemove = useCallback(
    (_, { index }: { index: number }) => {
      const newFeedbackLink = [...stateToUse].filter((_link, linkIndex) => index !== linkIndex);
      cbRef.current(newFeedbackLink);
      updater(newFeedbackLink);
    },
    [updater, cbRef, stateToUse],
  );

  const handleAdd = useCallback(() => {
    const newFeedbackLink = [...stateToUse, { enabled: false, type: 'google', url: '', key: uuid() }];
    cbRef.current(newFeedbackLink);
    updater(newFeedbackLink);
  }, [updater, cbRef, stateToUse]);

  const handleSelect = useCallback(
    (event, { index }: { index: number }) => {
      const newFeedbackLink = [...stateToUse].map((item, linkIndex) => {
        if (index === linkIndex) {
          return { ...item, enabled: event.target.checked };
        }
        return { ...item, enabled: false };
      });
      cbRef.current(newFeedbackLink);
      updater(newFeedbackLink);
    },
    [updater, cbRef, stateToUse],
  );

  const props = useMemo(
    () => ({
      value: stateToUse,
      onChange: handleChange,
      onRemove: handleRemove,
      onAdd: handleAdd,
      onSelect: handleSelect,
    }),
    [stateToUse, handleChange, handleRemove, handleAdd, handleSelect],
  );

  return {
    state: stateToUse,
    handleChange,
    handleRemove,
    handleAdd,
    handleSelect,
    props,
  };
}

export default withStyles(useStyles)(FeedbackLinks);
